import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Lock, Plus, Trash2, Tag } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function PromoCodesPanel({ onUpgrade }) {
  const { t } = useTranslation();
  const [locked, setLocked] = useState(false);
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ code: "", discount_type: "percent", discount_value: 10, max_uses: 0, expires_at: "" });

  const load = useCallback(() => {
    api.get("/loueur/promo-codes").then((r) => setCodes(r.data.promo_codes)).catch((e) => { if (e.response?.status === 403) setLocked(true); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/loueur/promo-codes", { ...form, discount_value: parseFloat(form.discount_value) || 0, max_uses: parseInt(form.max_uses) || 0, expires_at: form.expires_at || null });
      toast.success(t("promo.created")); setForm({ code: "", discount_type: "percent", discount_value: 10, max_uses: 0, expires_at: "" }); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/loueur/promo-codes/${id}`); load(); } catch (err) { toast.error(formatError(err.response?.data?.detail)); } };

  if (locked) return (
    <div data-testid="promo-locked" className="text-center py-12 bg-white rounded-2xl border border-border">
      <Lock className="w-8 h-8 text-canard mx-auto mb-3" />
      <p className="text-muted2 font-body mb-4">{t("promo.locked")}</p>
      <Button data-testid="promo-upgrade" onClick={onUpgrade} className="rounded-full bg-canard hover:bg-canard-dark font-bold">{t("litiges.upgrade")}</Button>
    </div>
  );

  return (
    <div data-testid="promo-panel" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <form onSubmit={create} className="bg-white rounded-2xl shadow-card border border-border p-5 space-y-3 h-fit">
        <p className="font-heading font-bold text-ink flex items-center gap-2"><Tag className="w-5 h-5 text-canard" /> {t("promo.new")}</p>
        <Input data-testid="promo-code-input" placeholder={t("promo.code")} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="h-11 rounded-xl bg-sand" required />
        <div className="flex gap-2">
          <select data-testid="promo-type" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="h-11 rounded-xl border border-border bg-sand px-3 text-sm font-bold">
            <option value="percent">%</option>
            <option value="fixed">€</option>
          </select>
          <Input data-testid="promo-value" type="number" placeholder={t("promo.value")} value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="h-11 rounded-xl bg-sand" required />
        </div>
        <Input data-testid="promo-max" type="number" placeholder={t("promo.maxUses")} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="h-11 rounded-xl bg-sand" />
        <div>
          <label className="text-xs font-bold text-muted2">{t("promo.expires")}</label>
          <Input data-testid="promo-expires" type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="h-11 rounded-xl bg-sand mt-1" />
        </div>
        <Button type="submit" data-testid="promo-create" className="rounded-full bg-canard hover:bg-canard-dark font-bold w-full"><Plus className="w-4 h-4 mr-1" /> {t("promo.create")}</Button>
      </form>

      <div className="space-y-3">
        {!codes.length && <div className="text-center py-10 bg-white rounded-2xl border border-border text-muted2 font-body">{t("promo.empty")}</div>}
        {codes.map((c) => (
          <div key={c.id} data-testid={`promo-${c.id}`} className="bg-white rounded-2xl border border-border p-4 flex items-center justify-between">
            <div>
              <p className="font-heading font-extrabold text-canard-dark tracking-wide">{c.code}</p>
              <p className="text-xs text-muted2">-{c.discount_value}{c.discount_type === "percent" ? "%" : "€"} · {c.uses}/{c.max_uses || "∞"} {t("promo.used")}{c.expires_at ? ` · ${t("promo.until")} ${c.expires_at}` : ""}</p>
            </div>
            <Button size="sm" variant="outline" data-testid={`promo-delete-${c.id}`} onClick={() => remove(c.id)} className="rounded-full h-8 text-destructive border-destructive/30"><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
